import { Button, Input, Stack } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useAuth } from "@/shared/session";
import { clientEntitySchema } from "../schemas/clientEntitySchema";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { Field } from "@/ui/chakra/field";
import { SimilarRecordsHint } from "@/ui/SimilarRecordsHint";
import { useSimilarClientEntities } from "../useCases/useSimilarClientEntities";

export type ClientEntityFormInput = z.infer<typeof clientEntitySchema>;

interface CreateClientEntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ClientEntityFormInput) => void;
  isSubmitting?: boolean;
}

export const CreateClientEntityModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}: CreateClientEntityModalProps) => {
  const { session } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<ClientEntityFormInput>({
    resolver: zodResolver(clientEntitySchema),
    defaultValues: {
      name: "",
      description: "",
      zones: [session?.zone ?? ""],
      enabled: true,
    },
  });

  const similarClientEntities = useSimilarClientEntities(
    watch("name"),
    session?.zone ?? undefined,
  );

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  const onFormSubmit = (data: ClientEntityFormInput) => {
    onSubmit(data);
  };

  return (
    <DialogRoot isOpen={isOpen} onClose={onClose} isCentered size="md">
      <DialogContent>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogHeader>
            <DialogTitle>Crear empresa cliente</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <Stack gap={4}>
              <Field
                label="Nombre"
                required
                invalid={!!errors.name}
                errorText={errors.name?.message}
              >
                <Input
                  {...register("name")}
                  placeholder="Nombre"
                  autoComplete="off"
                />
              </Field>
              <SimilarRecordsHint
                items={similarClientEntities}
                entityLabel="empresas"
                mt={-2}
              />
              <Field
                label="Descripción"
                invalid={!!errors.description}
                errorText={errors.description?.message}
              >
                <Input
                  {...register("description")}
                  placeholder="Descripción"
                  autoComplete="off"
                />
              </Field>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} type="button">
              Cancelar
            </Button>
            <Button colorPalette="brand" type="submit" loading={isSubmitting}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};
